import { PayrollNotes } from 'components/payroll/payroll-notes/PayrollNotes'
import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { API_PAYROLL_NOTES } from 'services/apiPayrollNotes'
import { useFetch } from 'services/useFetch'
import { constructOptionsSelect, getAllQueryParams, setQueryParams } from 'utils/utils'
import { ACTION_TYPE } from 'constants/constant'
import { INITIAL_DATA_QUERY_NOTES } from 'configs/payroll/configTablePayrollNotes'

const INITIAL_FORM_VALUE = {
    department: null,
    division: null,
    note: '',
}

export const PayrollNotesContainer = () => {
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const payrollId = searchParams.get('payrollId')
    const status = searchParams.get('status')
    const { payrollId: _payrollId, status: _status, ...otherParams } = getAllQueryParams(searchParams)
    const query = { ...INITIAL_DATA_QUERY_NOTES, ...otherParams }

    const [formValue, setFormValue] = useState(INITIAL_FORM_VALUE)
    const [actionType, setActionType] = useState(ACTION_TYPE.CREATE)
    const [editId, setEditId] = useState(null)
    const [deleteId, setDeleteId] = useState(null)
    const [departmentOptions, setDepartmentOptions] = useState([])
    const [divisionOptions, setDivisionOptions] = useState([])
    const [isDivisionLoading, setIsDivisionLoading] = useState(false)
    const [modal, setModal] = useState({
        form: false,
        delete: false,
        limit: false,
    })
    const [toast, setToast] = useState({
        isShowToast: false,
        message: '',
    })

    const {
        data: payrollNotesList,
        fetching: getPayrollNotesList,
        isLoading,
    } = useFetch({ ...API_PAYROLL_NOTES.LIST, initPathVariable: payrollId })

    const { fetching: getDepartmentList } = useFetch(API_PAYROLL_NOTES.DEPARTMENT_LIST)
    const { fetching: getDivisionList } = useFetch(API_PAYROLL_NOTES.DIVISION_LIST)
    const { fetching: createPayrollNotes, isLoading: isCreating } = useFetch(API_PAYROLL_NOTES.CREATE)
    const { fetching: updatePayrollNotes, isLoading: isUpdating } = useFetch(API_PAYROLL_NOTES.UPDATE)
    const { fetching: deletePayrollNotes, isLoading: isDeleting } = useFetch(API_PAYROLL_NOTES.DELETE)

    useEffect(() => {
        fetchPayrollNotesList()
    }, [location?.search])

    useEffect(() => {
        fetchDepartments()
    }, [])

    const fetchDepartments = async (search = '') => {
        try {
            const response = await getDepartmentList({ search })
            const rawData = response?.data || []
            const options = constructOptionsSelect(rawData, 'name', 'id')
            setDepartmentOptions(options)
            return options
        } catch (error) {
            return []
        }
    }

    const fetchDivisions = async (departmentId, search = '') => {
        if (!departmentId) {
            setDivisionOptions([])
            return []
        }
        setIsDivisionLoading(true)
        try {
            const response = await getDivisionList({ search }, departmentId)
            const rawData = response?.data || []
            const options = constructOptionsSelect(rawData, 'name', 'id')
            setDivisionOptions(options)
            return options
        } catch (error) {
            setDivisionOptions([])
            return []
        } finally {
            setIsDivisionLoading(false)
        }
    }

    const onSearchDepartment = (keyword) => {
        fetchDepartments(keyword)
    }

    const onSearchDivision = (keyword) => {
        const deptId = formValue.department?.value
        if (deptId) {
            fetchDivisions(deptId, keyword)
        }
    }

    const fetchPayrollNotesList = () => {
        getPayrollNotesList({ ...query })
    }

    const onChangeQuery = (queryParams) => {
        const params = setQueryParams({
            ...query,
            ...queryParams,
            ...(payrollId ? { payrollId } : {}),
            ...(status ? { status } : {}),
        })
        setSearchParams(params, { replace: true })
    }

    const clearToast = () => {
        setToast({ isShowToast: false, message: '' })
    }

    const showToast = (message) => {
        setToast({ isShowToast: true, message })
    }

    const onChangeDepartment = (selectedDepartment) => {
        const deptObj = selectedDepartment?.value || selectedDepartment
        setFormValue((prev) => ({
            ...prev,
            department: deptObj,
            division: null,
        }))
        const deptId = deptObj?.value || deptObj?.id
        if (deptId) {
            fetchDivisions(deptId)
        } else {
            setDivisionOptions([])
        }
    }

    const onChangeDivision = (selectedDivision) => {
        const divObj = selectedDivision?.value || selectedDivision
        setFormValue((prev) => ({
            ...prev,
            division: divObj,
        }))
    }

    const onChangeNotes = (e) => {
        setFormValue((prev) => ({
            ...prev,
            note: e?.value ?? e?.target?.value ?? '',
        }))
    }

    const onCreateNotesHandler = async () => {
        setActionType(ACTION_TYPE.CREATE)
        setEditId(null)
        setFormValue(INITIAL_FORM_VALUE)
        setDivisionOptions([])
        setModal((prev) => ({ ...prev, form: true }))
        if (departmentOptions.length === 0) {
            await fetchDepartments()
        }
    }

    const onClickItemHandler = async ({ item, actionType: type }) => {
        if (type === ACTION_TYPE.EDIT) {
            setActionType(ACTION_TYPE.EDIT)
            setEditId(item?.id)

            const deptId = item?.department?.id || null

            if (deptId) {
                await fetchDivisions(deptId)
            }

            const selectedDept = item?.department
                ? { label: item?.department?.name, value: item?.department?.id }
                : null

            const selectedDiv = item?.division ? { label: item?.division?.name, value: item?.division?.id } : null

            setFormValue({
                department: selectedDept,
                division: selectedDiv,
                note: item?.note || '',
            })
            setModal((prev) => ({ ...prev, form: true }))
        } else if (type === ACTION_TYPE.DELETE) {
            setDeleteId(item?.id)
            setModal((prev) => ({ ...prev, delete: true }))
        }
    }

    const onSubmitForm = () => {
        const payload = {
            department_id: formValue.department?.value,
            division_id: formValue.division?.value,
            note: formValue.note,
        }

        if (actionType === ACTION_TYPE.CREATE) {
            createPayrollNotes(payload, payrollId).then(() => {
                showToast('Payroll Notes berhasil dibuat')
                setModal((prev) => ({ ...prev, form: false }))
                fetchPayrollNotesList()
            })
        } else {
            updatePayrollNotes(payload, editId).then(() => {
                showToast('Payroll Notes berhasil diubah')
                setModal((prev) => ({ ...prev, form: false }))
                fetchPayrollNotesList()
            })
        }
    }

    const onDelete = () => {
        deletePayrollNotes({}, deleteId).then(() => {
            showToast('Payroll Notes berhasil dihapus')
            setModal((prev) => ({ ...prev, delete: false }))
            setDeleteId(null)
            if (payrollNotesList?.data?.result?.length === 1 && Number(query?.page) !== 1) {
                onChangeQuery({ page: 1 })
            } else {
                fetchPayrollNotesList()
            }
        })
    }

    const isFormValid = () => {
        return !!(formValue.department?.value && formValue.note && formValue.note.trim().length > 0)
    }

    const props = {
        dataList: payrollNotesList?.data,
        isLoading,
        query,
        onChangeQuery,
        onClickItemHandler,
        onCreateNotesHandler,
        modal,
        setModal,
        formValue,
        onChangeDepartment,
        onChangeDivision,
        onChangeNotes,
        onSearchDepartment,
        onSearchDivision,
        departmentOptions,
        divisionOptions,
        isDivisionLoading,
        actionType,
        isCreating,
        isUpdating,
        isDeleting,
        isFormValid,
        onSubmitForm,
        onDelete,
        toast,
        clearToast,
        status,
    }

    return <PayrollNotes {...props} />
}
